// Main dashboard page

import React from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useAnalytics, useContent, usePitches } from '../../hooks/useApi'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Badge } from '../../components/ui/Badge'
import { formatNumber, formatCredits } from '../../lib/utils'

// Icons for stats
const Icons = {
  Posts: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  ),
  Engagement: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  ),
  Content: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  Ideas: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
    </svg>
  ),
  Trending: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  ),
  Calendar: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  Analytics: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  )
}

const DashboardPage: React.FC = () => {
  const { user } = useAuth()
  const { data: analytics } = useAnalytics({ period: '30d' })
  const { data: contentData } = useContent()
  const { data: pitchesData } = usePitches()

  const stats = [
    {
      title: 'Total Posts',
      value: analytics?.overview?.totalPosts ?? 0,
      description: 'Posts published this month',
      icon: Icons.Posts,
      color: 'from-blue-500 to-blue-600',
      gradient: 'bg-gradient-to-r from-blue-500 to-blue-600'
    },
    {
      title: 'Total Engagement',
      value: formatNumber(analytics?.overview?.totalEngagement ?? 0),
      description: 'Likes, comments, and shares',
      icon: Icons.Engagement,
      color: 'from-pink-500 to-pink-600',
      gradient: 'bg-gradient-to-r from-pink-500 to-pink-600'
    },
    {
      title: 'Content Items',
      value: contentData?.content?.length ?? 0,
      description: 'Created content pieces',
      icon: Icons.Content,
      color: 'from-green-500 to-green-600',
      gradient: 'bg-gradient-to-r from-green-500 to-green-600'
    },
    {
      title: 'Daily Pitches',
      value: pitchesData?.pitches?.length ?? 0,
      description: 'AI-generated ideas today',
      icon: Icons.Ideas,
      color: 'from-purple-500 to-purple-600',
      gradient: 'bg-gradient-to-r from-purple-500 to-purple-600'
    }
  ]

  const quickActions = [
    {
      title: 'Generate Content Ideas',
      description: 'Get AI-powered content pitches for your business',
      icon: Icons.Trending,
      href: '/pitches',
      variant: 'default' as const,
      gradient: 'bg-gradient-to-r from-orange-500 to-red-500'
    },
    {
      title: 'Content Library',
      description: 'Manage your created content and schedule posts',
      icon: Icons.Content,
      href: '/library',
      variant: 'outline' as const,
      gradient: 'bg-gradient-to-r from-blue-500 to-indigo-500'
    },
    {
      title: 'Content Calendar',
      description: 'Schedule and organize your content publishing',
      icon: Icons.Calendar,
      href: '/calendar',
      variant: 'outline' as const,
      gradient: 'bg-gradient-to-r from-green-500 to-emerald-500'
    },
    {
      title: 'Analytics Dashboard',
      description: 'Track performance and engagement metrics',
      icon: Icons.Analytics,
      href: '/analytics',
      variant: 'outline' as const,
      gradient: 'bg-gradient-to-r from-purple-500 to-pink-500'
    }
  ]

  return (
    <div className="space-y-8 fade-in">
      {/* Welcome Header */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-hero opacity-10 rounded-3xl"></div>
        <div className="relative p-8 rounded-3xl bg-card/80 backdrop-blur-xl border border-border/50">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="space-y-2">
              <h1 className="text-4xl font-bold gradient-text">
                Welcome back, {user?.name || 'User'}! 👋
              </h1>
              <p className="text-lg text-muted-foreground">
                Here's what's happening with your content today
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Badge variant="secondary" className="text-sm">
                {user?.tenant?.creditsRemaining || 0} credits remaining
              </Badge>
              <div className="w-2 h-2 bg-green-500 rounded-full pulse-glow"></div>
              <span className="text-sm text-muted-foreground">Online</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="dashboard-grid">
        {stats.map((stat, index) => (
          <Card key={stat.title} className="stats-card slide-up hover-lift" style={{ animationDelay: `${index * 0.1}s` }}>
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <p className="stats-label">{stat.title}</p>
                <p className="stats-value">{stat.value}</p>
                <p className="text-xs text-muted-foreground">{stat.description}</p>
              </div>
              <div className={`p-4 rounded-2xl ${stat.gradient} shadow-lg`}>
                <stat.icon />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">Quick Actions</h2>
          <Button variant="outline" size="sm" className="btn-outline">
            View All
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {quickActions.map((action, index) => (
            <Card key={action.title} className="card-modern hover-lift slide-up" style={{ animationDelay: `${0.4 + index * 0.1}s` }}>
              <CardHeader className="pb-4">
                <div className={`w-12 h-12 rounded-2xl ${action.gradient} flex items-center justify-center mb-4 shadow-lg`}>
                  <action.icon />
                </div>
                <CardTitle className="text-lg">{action.title}</CardTitle>
                <CardDescription className="text-sm">
                  {action.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to={action.href}>
                  <Button 
                    variant={action.variant} 
                    className="w-full btn-primary"
                  >
                    Get Started
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Content Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Content */}
        <Card className="card-modern slide-up" style={{ animationDelay: '0.8s' }}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl">Recent Content</CardTitle>
              <Link to="/content">
                <Button variant="outline" size="sm" className="btn-outline">
                  View All
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {contentData?.content?.slice(0, 5).map((item, index) => (
                <div key={index} className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icons.Content />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{item.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {item.platform} • {new Date(item.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {item.status}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* AI Pitches */}
        <Card className="card-modern slide-up" style={{ animationDelay: '0.9s' }}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl">AI Content Ideas</CardTitle>
              <Link to="/pitches">
                <Button variant="outline" size="sm" className="btn-outline">
                  Generate More
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {pitchesData?.pitches?.slice(0, 3).map((pitch, index) => (
                <div key={index} className="p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                      <Icons.Ideas />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-sm mb-1">{pitch.title}</h4>
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {pitch.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <Badge variant="outline" className="text-xs">
                      {pitch.platform}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {pitch.type}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Activity Feed */}
      <Card className="card-modern slide-up" style={{ animationDelay: '1s' }}>
        <CardHeader>
          <CardTitle className="text-xl">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors">
              <div className="w-10 h-10 bg-green-500/10 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">Post published successfully</p>
                <p className="text-xs text-muted-foreground">2 hours ago</p>
              </div>
              <Badge variant="secondary" className="text-xs">LinkedIn</Badge>
            </div>
            
            <div className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors">
              <div className="w-10 h-10 bg-blue-500/10 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">New content idea generated</p>
                <p className="text-xs text-muted-foreground">4 hours ago</p>
              </div>
              <Badge variant="secondary" className="text-xs">AI</Badge>
            </div>
            
            <div className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors">
              <div className="w-10 h-10 bg-purple-500/10 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">Analytics report generated</p>
                <p className="text-xs text-muted-foreground">1 day ago</p>
              </div>
              <Badge variant="secondary" className="text-xs">Report</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default DashboardPage